import { useEffect, useRef, useState, useCallback } from "react"
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'

import { Translate } from "../../Components/Translator.jsx"
import { NowInDateTimeFormat, IsEmptyString, MakeOsLocaleInfoLongDateTime } from "../../Components/GeneralFunctions.jsx"

import CallNotificationHubspot from "./CallNotificationHubSpot.jsx"
import CallHistory from "../../Components/CallHistory.jsx"
import SpeedDials from "../../Components/SpeedDials.jsx"

import { CallNotesElement, PressSaveCall } from "./CallNotes.tsx"
import { useCti } from "./useCti.ts"

import HubspotInstaller from "./HubSpotInstaller.jsx"
import HubspotAuthorize from "./HubSpotAuthorize.jsx"
import VerifyRegistration from "./VerifyRegistration.jsx"
import { ClearAssociationsHsObject, RecognitionByNumber } from "./Utils.jsx";
import { jwtDecode } from "jwt-decode"

// Circumvents useCti's stale closure usage of the onDialEvent that gets updated by updating a called list rather than the funcion itself
var onDialAction = [() => {console.log("app not fully registered for user, cannot handle outgoing call yet.")}];
const onDialEvent = (callData) => { onDialAction[0](callData); };
const EmbeddedHubspot = (props) => {
  const [initExistingCall, setInitExistingCall] = useState(false);
  const [ctiInitialized, setCtiInitialized] = useState(false);
  const sdk = useCti(() => setInitExistingCall(true), () => setCtiInitialized(true), onDialEvent);
  
  // HubSpot secure install flow: branch on the `step` parameter.
  // https://developers.hubspot.com - "Migrate to the secure install flow".
  const urlParams = new URLSearchParams(window.location.search);
  const step = urlParams.get('step');

  // Step 1 - authorize: generate & store a state token bound to the signed-in CloudCTI
  // tenant, then redirect back to HubSpot with the state appended to returnUrl.
  if (step === "authorize")
      return HubspotAuthorize(props.Brand)

  // Step 2 - finalize: HubSpot returns with step=finalize + code + state; the state is
  // validated (server-side) before the code is exchanged. Legacy marketplace installs and
  // the manual "Reauthorize" callback also land here with just a `code` (no step).
  if ((step === "finalize") || urlParams.get('code'))
      return HubspotInstaller(props.Brand)

  // Check if user's tenant is registered for his portalId on the Hubspot Proxy server
  const regMsg = VerifyRegistration(props.Brand);
  if (regMsg != "valid")
    return regMsg;
  else {
    var newProps = {...props};
    newProps.sdk = sdk;
    newProps.initExistingCall = initExistingCall;
    newProps.ctiInitialized = ctiInitialized;
    return <EmbeddedHubspotBody {...newProps}></EmbeddedHubspotBody>;
  }
}

var currentCallIdNoState = "";
const EmbeddedHubspotBody = (props) => {
  // Calling stuff
  const [callCopy, setCallCopy] = useState(null); // Used for both in- and outbound calls
  const [callAnswered, setCallAnswered] = useState(false);
  const outBoundName = useRef(null);
  const outBoundObjectId = useRef(null);
  const [forceRepaint, setForceRepaint] = useState(0); // Force repaint of the component when a call is made, to update Notes title

  //const [hubspotBroadcastChannelEmbedded, SetHubspotBroadcastChannelEmbedded] = useState(new BroadcastChannel("cloudctiwebclient2024_hubspot_embedded"));
  
  const callCopyRef = useRef(null);
  useEffect(() => {
    callCopyRef.current = callCopy;
  }, [callCopy]);

  var recognitionApplied = false;

  // PhoneNumber from cti is still empty by the time this is called
  const handleOutboundCall = useCallback(async (callData) => {
    outBoundName.current = callData.name;
    outBoundObjectId.current = callData.objectId;
    props.OnMakeCall(callData.phoneNumber, callData?.name ?? null, null, null); // OnMakeCall fills in the parameters if we can't get all of the last 3
  }, []);

  useEffect(() => {
    if (props.initExistingCall)
      console.error("initializeCallingStateForExistingCall triggered; not implemented");
  }, [props.initExistingCall]);

  useEffect(() => {
    if (!props.ctiInitialized)
      return;

    // Called after cti is initialized
    console.log("Initializing HubSpot app");
    cti.userLoggedIn();
    cti.userAvailable();
    onDialAction[0] = handleOutboundCall;

    window.addEventListener("storage", (event) => {
        if (event.key === "broadcast_hubspot_embedded") 
        {
          try
          {
            const msg = JSON.parse(event.newValue);            

            if (msg.type === "notesChanged") 
            {
              console.log("broadcast_hubspot_embedded storage event notesChanged")
              setForceRepaint(forceRepaint + 1);
            }
            else if (msg.type === "notesSaved") 
            {
              console.log("broadcast_hubspot_embedded storage event notesSaved")
              const newNotes = localStorage.getItem("hb_stashedNotes");
              textAreaRef.current ? textAreaRef.current.value = newNotes : console.log("textAreaRef.current was null; cannot update notes.");
              setForceRepaint(forceRepaint + 1);
            }
          }
          catch {}
        }
    });

    // Set up the broadcast channel for embedded HubSpot
    /*
    hubspotBroadcastChannelEmbedded.addEventListener("message", function(event) {
      console.log("hubspotBroadcastChannelEmbedded event", event.data)
      if (event.data.startsWith("notesChanged")) {
        const newNotes = localStorage.getItem("hb_stashedNotes");
        textAreaRef.current ? textAreaRef.current.value = newNotes : console.log("textAreaRef.current was null; cannot update notes.");
        setForceRepaint(forceRepaint => forceRepaint + 1);
      }
      else if (event.data.startsWith("notesSaved")) {
        setForceRepaint(forceRepaint => forceRepaint + 1);
      }
    });
    */

    // Read out the last call history entry
    const lastCall = localStorage.getItem("newestcallevent");
    if (lastCall != null) {
      var lastCallJson = JSON.parse(lastCall);
      if (lastCallJson.Direction.toLowerCase() === "inbound")
        HandleCallEvent(lastCallJson, true);
      else if (lastCallJson.Direction.toLowerCase() === "outbound")
        HandleCallEvent(lastCallJson, false);
      setCallCopy(lastCallJson);
    }
  }, [props.ctiInitialized]);

  const [mHubspotTab, SetHubspotTab] = useState(localStorage.getItem("hb_tab") || "Main")
  const { cti, engagementId, incomingMatches, setIncomingMatches, setEngagementId } = props.sdk;
  const [previousCallEngagementId, setPreviousCallEngagementId] = useState(null);

  SetCtiBroadcast();

  const HubspotTabChange = (aTab) =>
  {
    if (mHubspotTab === "Main") 
      localStorage.setItem("hb_stashedNotes", textAreaRef.current != null ? textAreaRef.current.value : "");
    else if (aTab == "Main") 
      if (textAreaRef.current != null)
        textAreaRef.current.value = localStorage.getItem("hb_stashedNotes");
    
    SetHubspotTab(aTab)
    localStorage.setItem("hb_tab", aTab)
  }

  useEffect(() => {
    if (mHubspotTab === "History")
      AdjustStyles()

    async function AdjustStyles() {
      let callHistoryItemText = document.getElementsByClassName("callhistoryitemtext")
      // Wait for page to load
      let tries = 0
      while (!callHistoryItemText || tries >= 50) {
        await new Promise(res => setTimeout(res, 200))
        callHistoryItemText = document.getElementById("callhistoryitemtext")
        tries++
      }
      if (callHistoryItemText) {
        for (let i = 0; i < callHistoryItemText.length; i++) {
          var cStyle = callHistoryItemText[i].style;
          callHistoryItemText[i].style.maxHeight = "39px";
          callHistoryItemText[i].style.display = "-webkit-box";
          callHistoryItemText[i].style.webkitLineClamp = "2";
          callHistoryItemText[i].style.webkitBoxOrient = "vertical";
          callHistoryItemText[i].style.overflow = "hidden";
          callHistoryItemText[i].style.overflowWrap = "anywhere";
        }
      }
    }
  }, [mHubspotTab]);


  // Call notes
	const textAreaRef = useRef(null);
	const engagementIdRef = useRef(engagementId);
	engagementIdRef.current = engagementId;


  // Handle call event changes
  useEffect(() => {
    // Initial empty variable trigger
    if (props.LastCallEventId == null || props.LastCallEventId === "")
      return;

    // Something went wrong with the localstorage
    const newCallStr = localStorage.getItem("newestcallevent");
    if (newCallStr == null || newCallStr === "") {
      console.error("No call event found in local storage, cannot handle call event");
      return;
    }

    var call = JSON.parse(newCallStr);
    HandleCallEvent(call, call.Direction.toLowerCase() === "inbound");
    
    const splitId = props.LastCallEventId.split(".")[0]; // Without state appended

    if (props.PrimaryTab) {
      if (splitId != currentCallIdNoState)
        AddCallToHbHistory();
      else // Update the name of the first entry in the call history if it is still just "Unknown caller" and we have a valid number
        CheckIfUpdateLastHistoryEntry();
    }
    
    currentCallIdNoState = splitId;

    // Add call to app history
    function AddCallToHbHistory() {
      var callHistoryHb = JSON.parse(localStorage.getItem("hb_callhistory") ?? "[]");
      if(!callHistoryHb) {
        console.error("Call history missing or corrupted, can't add call to history");
        return;
      }
      
      // Remove the oldest call if more than 50 entries are in history
      if (callHistoryHb.length >= 50)
          callHistoryHb = callHistoryHb.pop();

      call.Application = "Hubspot";
      call.ScriptName = "";
      call.Scripts = [];
      call.Incoming = call.Direction.toLowerCase() === "inbound";
      call.DateTime = NowInDateTimeFormat();
      
      const externalNumber = call.Direction.toLowerCase() === "inbound" ? call.CallerNumber : call.CalledNumber;
      call.Number = externalNumber;
      call.Name = Translate("Unknown caller") + ( /\d/.test(externalNumber) ? " " + externalNumber : ""); // Write number if it contains digits, otherwise it's probably empty or a name
      callHistoryHb.unshift(call);
      localStorage.setItem("hb_callhistory", JSON.stringify(callHistoryHb));
    }

    async function CheckIfUpdateLastHistoryEntry() {
      var callHistoryHb = JSON.parse(localStorage.getItem("hb_callhistory") ?? "[]");
      const externalNumber = call.Direction.toLowerCase() === "inbound" ? call.CallerNumber : call.CalledNumber;
      // Only update if the number was previously unknown
      if (callHistoryHb[0].Name !== "Unknown caller" || !/\d/.test(externalNumber))
        return;
      
      callHistoryHb[0].Name = "Unknown caller " + externalNumber;
      if (call.Direction.toLowerCase() === "outbound") {
        callHistoryHb[0].CalledNumber = externalNumber

        // Update the recognition
        // (This didn't happen in the engagementId UseEffect, because the phonenumber wasn't available yet)
        await ApplyManualRecognition(externalNumber);

        if (call.State.toLowerCase() === "ended" && localStorage.getItem("hb_callMatches") != []) { // Save call under match name
          const newMatches = JSON.parse(localStorage.getItem("hb_callMatches"));
          PressSaveCall(cti, newMatches, textAreaRef, engagementId, newMatches[0]);
        }
      }
      else 
        callHistoryHb[0].CallerNumber = externalNumber;

      localStorage.setItem("hb_callhistory", JSON.stringify(callHistoryHb));
    }
  }, [props.LastCallEventId])


  async function HandleCallEvent(callEvent, isInbound) {
    if (callEvent == null)
      return;

    if (callEvent.State.toLowerCase() === "ringing") {
      setCallAnswered(false);

      cti.userUnavailable();
      
      // Save and reset previous call notes
      if (callCopy != null && callCopy.CallId != callEvent.CallId && 
      previousCallEngagementId != null) {
        const matchObjectIdStr = localStorage.hb_selectedMatchObjectId; // Still from previous call
        var previousCallMatch;
        if (!isNaN(matchObjectIdStr)) {
          const matchObjectId = Number(matchObjectIdStr);
          previousCallMatch = incomingMatches.filter(x => x.objectCoordinates.objectId == matchObjectId)[0]; // Should always contain exactly 1 match
        }
        else
          previousCallMatch = null;
        if (props.PrimaryTab) 
          PressSaveCall(cti, incomingMatches, textAreaRef, previousCallEngagementId, previousCallMatch);
      }
      ResetState();

      let toNumberStr = callEvent.CalledNumber;
      // Needs to be a valid telephone number, or Hubspot will not initiate a call Match event and call id generation gets messy
      if (toNumberStr == null || toNumberStr === "" || toNumberStr.length < 8) {
        const securityToken = localStorage.getItem("securitytoken");
        const decodedToken = jwtDecode(securityToken)
        toNumberStr = decodedToken.UserName ?? decodedToken.Email;
      }
      const callDetails = {
        createEngagement: true,
        fromNumber: callEvent.CallerNumber,
        toNumber: toNumberStr,
        externalCallId: callEvent.CallId
      }
      
      let externalNumber;
      if (isInbound) {
        cti.incomingCall(callDetails);
        externalNumber = callEvent.CallerNumber;
      }
      else
      {
        cti.outgoingCall(callDetails);
        externalNumber = callEvent.CalledNumber;
      }

      // Switch to main tab on incoming call
      HubspotTabChange("Main");

      await ApplyManualRecognition(externalNumber);
      
      outBoundName.current = null;
      outBoundObjectId.current = null;
      setCallCopy(callEvent);
    }
    else if (callEvent.State.toLowerCase() === "answered") {
      setCallAnswered(true);
      setCallCopy(callEvent);
      cti.callAnswered();
    }
    else if (callEvent.State.toLowerCase() === "ended") {
      // https://developers.hubspot.com/docs/guides/api/crm/extensions/calling-sdk#callended
      cti.callEnded({
        callEndStatus: callAnswered ? "COMPLETED" : (isInbound ? "MISSED" : "NO_ANSWER")
      });

      var callDuration = callEvent.AnswerTime != null ? (callEvent.EndTime - callEvent.AnswerTime) : 0;
      cti.callCompleted({
        engagementId: engagementIdRef.current,
        hideWidget: false,
        // https://developers.hubspot.com/docs/api/crm/calls#update-calls
        // https://developers.hubspot.com/docs/guides/api/crm/engagements/calls#properties
        engagementProperties: {
          // "hsCallStatus must be defined in the event payload.", but still causes error if defined...
          // hs_call_status: "COMPLETED"
          hs_call_duration: callDuration,
          hs_call_disposition: callAnswered ? "f240bbac-87c9-4f6e-bf70-924b57d47db7" : "73a0d17f-1163-4015-bdd5-ec830791da20" // Connected or No answer
          // hs_call_recording_url: null,
          // hs_call_title: "Test title",
        },
      });
      // Instantly setting availability causes an 'update call status failed'(?)
      cti.userAvailable();
      setPreviousCallEngagementId(engagementIdRef.current);
      if (props.PrimaryTab)
        await PressSaveCall(cti, incomingMatches, textAreaRef, engagementId);

      setCallCopy(callEvent);
    }
  }

  const RemoveFromHubspotCallHistory = (callid) =>
  {
    console.log("RemoveFromHubspotCallHistory",callid)
    var callHistoryHb = JSON.parse(localStorage.getItem("hb_callhistory") ?? "[]");
    if(!callHistoryHb) { return; }

    callHistoryHb = callHistoryHb.filter((call) => call.CallId !== callid)

    localStorage.setItem("hb_callhistory", JSON.stringify(callHistoryHb));

    setForceRepaint(forceRepaint => forceRepaint + 1);
  }

  function ResetState() {
    // Newly opened tabs shouldn't influence localStorage
    if (props.PrimaryTab) {
      localStorage.removeItem("hb_stashedNotes");
      localStorage.removeItem("hb_selectedIndex");
      localStorage.removeItem("hb_callMatches");
      localStorage.removeItem("hb_lastAssociatedObjectId");
      localStorage.removeItem("hb_lastAssociatedCallId");
      localStorage.removeItem("hb_selectedMatchObjectId");
      localStorage.setItem("hb_notesSaved", "true");
    }
    setIncomingMatches([]);
    setEngagementId(null);
    setPreviousCallEngagementId(null);
    if (textAreaRef.current != null)
      textAreaRef.current.value = "";
    recognitionApplied = false;
  }

  async function ApplyManualRecognition(externalNumber) {
    const broadCastNotif = new BroadcastChannel("cloudctiwebclient2024_hubspot_notification");
    const broadCastCti = new BroadcastChannel("cloudctiwebclient2024_hubspot_cti");

    if (props.PrimaryTab)
    {
      // Some phone 'numbers' are actually names, so we check that it at least contains digits
      if (externalNumber == null || externalNumber === "" || !(/\d/.test(externalNumber))) {
        console.log("Number " + externalNumber + " invalid, no recognition requested.");
      }
      else if (!recognitionApplied) {
        // Request recognitions if not already provided
        const recognitionList = (await RecognitionByNumber(externalNumber)).results;

        recognitionList.forEach(result => {
          result.objectCoordinates.portalId = localStorage.hb_portalId;
        });

        if (recognitionList.length > 0) {
          localStorage.setItem("hb_selectedIndex", "0");
          var newSelectIndex = 0;

          // Set select index if we can recognise the object the user was calling
          for (var i = 0; i < recognitionList.length; i++) {
            const result = recognitionList[i];
            var name = "";
            if (result.callerIdType === "CONTACT")
              name = result.firstName + " " + result.lastName;
            else if (result.callerIdType === "COMPANY")
              name = result.name;
            if (outBoundName != null && (name === outBoundName.current || result.objectCoordinates.objectId === outBoundObjectId.current)) {
              newSelectIndex = i;
              break;
            }
          }
          setTimeout(() => {
            broadCastNotif.postMessage("selectIndexChanged:" + newSelectIndex);
          }, 500);
        }
        
        // Make the new matches available in local storage and notify other tabs
        setIncomingMatches(recognitionList);
        localStorage.setItem("hb_callMatches", JSON.stringify(recognitionList));
        broadCastNotif.postMessage("matchesChanged");
        broadCastCti.postMessage("matchesChanged");
        recognitionApplied = true;
      }
    }
  }

  // Set up notes element
  var notesElement = <div></div>;

  // Add engagementId to app history once it's created
  // Sometimes the engagementId of previous usage is provided, which is why history length != 0 is also checked 
  useEffect(() => {
    // Only one tab needs to be adjusting the call history and associations
    if (!props.PrimaryTab)
      return;

    var callHistory = localStorage.getItem("hb_callhistory");
    if (engagementId == null || callHistory == undefined || callHistory.length == 0)
      return;

    // Hubspot sometimes automatically associates with the an object, so we need to disassociate it first
    // The logic behind the association is unclear, so we clear all associations for this call
    ClearAssociationsHsObject(engagementId);
 
    // Update the first entry in the call history with the engagementId
    var callHistoryHb = JSON.parse(callHistory ?? "[]");
    callHistoryHb[0].EngagementId = engagementId;
    localStorage.setItem("hb_callhistory", JSON.stringify(callHistoryHb));
  }, [engagementId]);

  // Show notes if a call is available
  if (callCopy)
    notesElement = CallNotesElement(props.Brand, cti, incomingMatches, textAreaRef, previousCallEngagementId!= null ? {current: previousCallEngagementId} : engagementIdRef, setForceRepaint);

  document.getElementById("callingnotes")?.focus();
  const mainbodyStyle = mHubspotTab === "Main" ? { } : { display: "none" };
  return (
    <table width="100%" height="100%">
    <tbody>
    <tr height="24">
        <td width="33%" className={mHubspotTab === "Main" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => HubspotTabChange("Main")}><span className="up3px">{Translate("Main").toUpperCase()}</span></td>
        <td width="34%" className={mHubspotTab === "History" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => HubspotTabChange("History")}><span className="up3px">{Translate("History").toUpperCase()}</span></td>
        <td width="33%" className={mHubspotTab === "Speed dial" ? "mobilemodewindow mobilemodewindowselected" : "mobilemodewindow"} onClick={() => HubspotTabChange("Speed dial")}><span className="up3px">{Translate("Speed dial").toUpperCase()}</span></td>
    </tr>
    <tr height="100%">
      { // Main tab. always runs, collapsed when not selected
      <td colSpan="3" valign="top" style={mainbodyStyle}>
        <div valign="center" align="left" className="padded10">
          <div className={!IsEmptyString(props.PhoneApiError) ? "notification notificationerror" : (callCopy == null || callCopy == undefined) && props.Brand == "bonline" ? "" : "notification"}>
              {!IsEmptyString(props.PhoneApiError) ? <div>{Translate("Failed to subscribe for phone events")}</div> : <span/>}
              {!IsEmptyString(props.PhoneApiError) ? Translate("Error") + ": " + props.PhoneApiError : 
                <CallNotificationHubspot 
                  incomingMatches={incomingMatches} 
                  setIncomingMatches={setIncomingMatches} 
                  callCopy={callCopy}
                  cti={cti} engagementIdRef={engagementIdRef} 
                  callTime={callCopy ? MakeOsLocaleInfoLongDateTime(new Date(callCopy.StartTime)) : ""} 
                  MakeCall={handleOutboundCall}
                  PressSaveCall={PressSaveCall}
                  textAreaRef={textAreaRef}
                  Brand={props.Brand}
                />}
          </div>
          {notesElement}
        </div>
        {/* <div>
          <button onClick={() => { 
            setIncomingMatches(dummyRec); 
            }}>
              Set multiple matches</button>
          <button onClick={() => { setIncomingMatches([dummyRec[0]]); }}>Set single match</button>
          <button onClick={() => { setIncomingMatches([]); }}>Reset matches</button>
        </div> */}
      </td> }

      { mHubspotTab === "History" ? 
        <td colSpan="3" align="left" valign="top">
          <CallHistory 
            Calls={JSON.parse(localStorage.getItem("hb_callhistory") ?? "[]")} 
            QueryingRecognitionNumbers={props.RecognitionCacheQueryingNumbers}
            SpeedDials={((props.SpeedDials === undefined) || (props.SpeedDials === null) || !props.SpeedDials) ? [] : props.SpeedDials} 
            ShowHeader={false} 
            AddSearch={true}
            OnMakeCall={(number, name, application, scripts) => handleOutboundCall({ phoneNumber: number, name: name, application: application, scripts: scripts })} 
            OnAddToSpeedDial={(number, name, application, scripts) => props.OnAddToSpeedDial(number, name, application, scripts)}
            OnRemoveFromSpeedDial={(number) => props.OnRemoveFromSpeedDial(number)} 
            OnRemoveFromCallHistory={(callid) => RemoveFromHubspotCallHistory(callid)}
          />
        </td>
      : (mHubspotTab === "Speed dial" ? 

        // Speed dial
        <td colSpan="3" align="left" valign="top">
          <SpeedDials 
            SpeedDials={((props.SpeedDials === undefined) || (props.SpeedDials === null) || !props.SpeedDials) ? [] : props.SpeedDials} 
            ShowHeader={false} 
            OnMakeCall={(number, name, application, scripts) => handleOutboundCall({ phoneNumber: number, name: name, application: application, scripts: scripts })}
            OnRemoveFromSpeedDial={(number) => props.OnRemoveFromSpeedDial(number)}
          />
        </td>
       : <></>)}
    </tr>
    </tbody>
    </table>
  )

  // Submethod to organise code
	function SetCtiBroadcast() {
		cti.broadcastChannel.onmessage = ({
			data,
		  }) => {
			// Send SDK message to HubSpot in the calling window
			if (cti.isFromWindow) {
        console.log("Received message from in CTI: ", data);
			  if (data.type === thirdPartyToHostEvents.INITIALIZED) {
          cti.contract.initialized(data.payload);
        } else if (data.type === thirdPartyToHostEvents.LOGGED_IN) {
          cti.contract.userLoggedIn();
        } else if (data.type === thirdPartyToHostEvents.LOGGED_OUT) {
          cti.contract.userLoggedOut();
        } else if (data.type === thirdPartyToHostEvents.USER_AVAILABLE) {
          cti.contract.userAvailable();
        } else if (data.type === thirdPartyToHostEvents.USER_UNAVAILABLE) {
          cti.contract.userUnavailable();
			  } else if (data.type === thirdPartyToHostEvents.INCOMING_CALL) {
          console.log("Incoming call detected, this is " + (props.PrimaryTab ? "" : " not ") + "the tab that will handle it");
          cti.externalCallId = uuidv4();
          cti.contract.incomingCall({
            ...data.payload,
            externalCallId: cti.externalCallId,
          });
			  } else if (data.type === thirdPartyToHostEvents.OUTGOING_CALL_STARTED) {
          console.log("Outgoing call detected, this is " + (props.PrimaryTab ? "" : " not ") + "the tab that will handle it");
          cti.externalCallId = uuidv4();
          cti.contract.outgoingCall({
            ...data.payload,
            externalCallId: cti.externalCallId,
          });
			  } else if (data.type === thirdPartyToHostEvents.CALL_ENDED) {
          cti.contract.callEnded({
            ...data.payload,
            externalCallId: cti.externalCallId,
          });
			  } else if (data.type === thirdPartyToHostEvents.CALL_COMPLETED) {
          cti.contract.callCompleted({
            ...data.payload,
            externalCallId: cti.externalCallId,
          });
			  }
			}
		};
	}
}

var dummyRec = [
  {
    "callerIdType": "CONTACT",
    "objectCoordinates": {
      "portalId": 143733925,
      "objectTypeId": "0-1",
      "objectId": 7686588371
    },
    "firstName": "John",
    "lastName": "D",
    "email": null
  },
  {
    "callerIdType": "CONTACT",
    "objectCoordinates": {
      "portalId": 143733925,
      "objectTypeId": "0-1",
      "objectId": 7090198730
    },
    "firstName": "John",
    "lastName": "Doe",
    "email": "jdoe@mail.com"
  },
  {
    "callerIdType":"COMPANY",
    "objectCoordinates": {
      "portalId":143733925,
      "objectTypeId":"0-2",
      "objectId":10290949870
    },
    "name":"Google"
  }
];

EmbeddedHubspotBody.propTypes = {
  LastCallEventId: PropTypes.string,
  PhoneApiError: PropTypes.string,
  PrimaryTab: PropTypes.bool,
  RecognitionCacheQueryingNumbers: PropTypes.array,
  SpeedDials: PropTypes.array,
  OnAddToSearchHistory: PropTypes.func,
  OnAddToSpeedDial: PropTypes.func,
  OnRemoveFromCallHistory: PropTypes.func,
  OnRemoveFromSpeedDial: PropTypes.func,
  Brand: PropTypes.string
}

export default EmbeddedHubspot